/**
 * Volver a la ruta que se quería abrir después del login (feedback 30/09: un
 * link a /performance/dtalk sin sesión te mandaba a loguearte y al terminar
 * aterrizabas en la home).
 *
 * El login sale de la SPA (Hosted UI de Cognito → Google) y vuelve a `/` por
 * redirect, así que la ruta no puede viajar en el state del router: se guarda
 * en sessionStorage (sobrevive al redirect en la misma pestaña y no se mezcla
 * entre pestañas) con un vencimiento corto, para que un login abandonado no te
 * lleve a una ruta vieja días después. Si el storage está bloqueado, se pierde
 * el "volver" y se cae en la home como antes — nunca rompe el login.
 */
const KEY = 'winclap.returnTo';
const TTL_MS = 15 * 60 * 1000;

/** Solo rutas internas: nada de `//otro-dominio`, ni la home, ni el login. */
function isWorthReturning(path: string): boolean {
  if (!path.startsWith('/') || path.startsWith('//')) return false;
  const pathname = path.split(/[?#]/)[0];
  return !['/', '/dashboard', '/login', '/unauthorized'].includes(pathname);
}

// Cerrar sesión a mano no es "me mandaron a loguearme": el render sin sesión
// que sigue al logout no tiene que guardar la página (en una PC compartida, el
// próximo login aterrizaría ahí). Vive hasta que la página se recarga, que es
// lo que hace el login con Google.
let explicitLogout = false;
export function markExplicitLogout(): void {
  explicitLogout = true;
  try { sessionStorage.removeItem(KEY); } catch { /* idem */ }
}

export function rememberReturnTo(location: { pathname: string; search?: string; hash?: string }): void {
  if (explicitLogout) return;
  const path = `${location.pathname}${location.search ?? ''}${location.hash ?? ''}`;
  if (!isWorthReturning(path)) return;
  try {
    sessionStorage.setItem(KEY, JSON.stringify({ path, at: Date.now() }));
  } catch { /* storage bloqueado: se cae en la home */ }
}

/** Lee sin borrar (se puede llamar en render). */
export function peekReturnTo(): string | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const { path, at } = JSON.parse(raw) as { path?: unknown; at?: unknown };
    if (typeof path !== 'string' || typeof at !== 'number' || Date.now() - at > TTL_MS || !isWorthReturning(path)) {
      sessionStorage.removeItem(KEY);
      return null;
    }
    return path;
  } catch {
    return null;
  }
}

/** Lee y borra: se usa una sola vez, al volver del login. */
export function consumeReturnTo(): string | null {
  const path = peekReturnTo();
  try { sessionStorage.removeItem(KEY); } catch { /* idem */ }
  return path;
}
